import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import img1 from "../../images/AE-service-img-1.jpeg";
import img2 from "../../images/AE-service-img-2.jpeg";
import img3 from "../../images/AE-service-img-3.jpeg";
import img5 from "../../images/AE-img-5.jpg";
import img6 from "../../images/AE-service-img-4.jpeg";
import img7 from "../../images/AE-img-9.jpeg";
import img8 from "../../images/AE-img-12.jpeg";
import Logo from "../../images/AE-Hero.svg";

const ServicesTwo = () => {
  const swiperRef = useRef(null);
  const swiperInstance = useRef(null);

  useEffect(() => {
    // Refresh AOS animations when component mounts
    if (typeof window !== 'undefined') {
      const AOS = require('aos');
      AOS.refresh();
    }

    // Initialize Swiper only if ref exists
    if (swiperRef.current && !swiperInstance.current) {
      // Swiper configuration
      const swiperConfig = {
        modules: [Navigation, Pagination, Autoplay],
        loop: true,
        speed: 600,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        slidesPerView: 'auto',
        spaceBetween: 40,
        centeredSlides: false,
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        },
        navigation: {
          nextEl: '.js-custom-next',
          prevEl: '.js-custom-prev',
        },
        breakpoints: {
          320: {
            slidesPerView: 1,
            spaceBetween: 40,
          },
          1200: {
            slidesPerView: 3,
            spaceBetween: 40,
          },
        },
      };

      // Initialize Swiper
      swiperInstance.current = new Swiper(swiperRef.current, swiperConfig);
    }

    // Cleanup on unmount
    return () => {
      if (swiperInstance.current) {
        swiperInstance.current.destroy();
        swiperInstance.current = null;
      }
    };
  }, []);

  const energyServices = [
    {
      id: 1,
      title: 'Large-Scale Industrial Solar Arrays',
      description: 'End-to-end design and installation of expansive rooftop solar systems for industrial clients — engineered for maximum output, reliability, and long-term return on investment.',
      image: img1
    },
    {
      id: 2,
      title: 'Electrical Infrastructure & Pole Installations',
      description: 'Our certified team handles electrical infrastructure work including pole mounting, cable routing, and outdoor distribution systems for residential and commercial sites.',
      image: img2
    },
    {
      id: 3,
      title: 'Distribution Board Inspections & Upgrades',
      description: 'Thorough inspection, labelling, and upgrading of electrical distribution boards to ensure compliance, safety, and optimal load management.',
      image: img3
    },
    {
      id: 4,
      title: 'Overhead Line & Utility Pole Work',
      description: 'Specialised high-reach electrical work including overhead line installations and repairs, carried out safely by our fully equipped and certified field crews.',
      image: img5
    },
    {
      id: 5,
      title: 'Commercial & Retail Electrical Fit-Outs',
      description: 'Full electrical fit-out services for retail and hospitality clients — from signage wiring and external lighting to complete internal electrical commissioning.',
      image: img7
    },
    {
      id: 6,
      title: 'Industrial LED Lighting Installations',
      description: 'Large-scale LED high-bay lighting solutions for warehouses and industrial facilities, delivering significant energy savings and improved workplace visibility.',
      image: img8
    },
    {
      id: 7,
      title: 'Commercial Rooftop Solar Installations',
      description: 'High-performance solar panel arrays designed and installed for commercial buildings — maximising roof space and reducing operational energy costs.',
      image: img6
    },
  ];

  return (
    <section id="services-2" className="services-2 section dark-background py-16 relative">
      {/* Logo top right */}
      <div className="absolute top-6 right-6 z-10">
        <img src={Logo} alt="Alternative Energy" className="h-10 w-auto md:h-14 opacity-80" />
      </div>
      {/* Section Title - Updated for integrated services */}
      <div className="container mx-auto p-4 section-title" data-aos="fade-up">
        <p className="text-xl text-center mb-12 text-gray-600">
          Integrated Electrical, Solar & Generator Services for Every Need
        </p>
      </div>

      <div className="services-carousel-wrap" data-aos="fade-up">
        <div className="container mx-auto px-4">
          <div className="swiper init-swiper relative" ref={swiperRef}>
            <div className="swiper-wrapper">
              {energyServices.map((service) => (
                <div key={service.id} className="swiper-slide">
                  <div className="service-item relative overflow-hidden rounded-lg group cursor-pointer">
                    <div className="service-item-contents absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10 flex flex-col justify-end p-6 transition-all duration-300 opacity-0 group-hover:opacity-100">
                      <Link to="#" className="text-white no-underline">
                        <span className="service-item-category text-sm font-medium text-orange-400 mb-2 block">
                          Integrated Service
                        </span>
                        <h2 className="service-item-title text-2xl font-bold">
                          {service.title}
                        </h2>
                        <p className="text-gray-200 text-sm mt-2">
                          {service.description}
                        </p>
                        <div className="flex space-x-2 mt-3">
                          <span className="px-2 py-1 bg-orange-600 text-white text-xs rounded">Solar</span>
                          <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Electrical</span>
                        </div>
                      </Link>
                    </div>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="img-fluid w-full h-[380px] object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Buttons */}
            <button className="navigation-prev js-custom-prev absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white hover:bg-orange-500 text-black hover:text-white p-3 rounded-full transition-all duration-300 -ml-6">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            <button className="navigation-next js-custom-next absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white hover:bg-orange-500 text-black hover:text-white p-3 rounded-full transition-all duration-300 -mr-6">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Pagination */}
            <div className="swiper-pagination mt-8 !relative !bottom-0"></div>
          </div>

          {/* Services Summary - Added to match other components */}
          <div className="mt-16 max-w-4xl mx-auto text-center" data-aos="fade-up" data-aos-delay="200">
            <div className="rounded-xl p-8 border border-gray-700">
              <h3 className="text-2xl font-bold text-orange-400 mb-6">Why Choose Our Integrated Energy Solutions?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-gray-600 font-semibold mb-2">Single Point of Contact</h4>
                  <p className="text-gray-400 text-sm">One team manages your solar, electrical, and generator needs</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <h4 className="text-gray-600 font-semibold mb-2">Unified Warranty</h4>
                  <p className="text-gray-400 text-sm">Comprehensive coverage for your entire energy system including generators</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-gray-600 font-semibold mb-2">Optimized Performance</h4>
                  <p className="text-gray-400 text-sm">Seamless integration of solar, electrical, and backup generator power maximizes efficiency and savings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesTwo;